/* PDF import — pick a PDF, render each page to a PNG data URL,
 * insert as image elements arranged in a vertical stack.
 *
 * Uses pdfjs-dist with a CDN-hosted worker. Page renderer at 1.5x scale for readable text.
 */
import { newImage } from "./wbUtils";

let pdfjsLib = null;

async function loadPdfJs() {
  if (pdfjsLib) return pdfjsLib;
  const mod = await import("pdfjs-dist/build/pdf.mjs");
  // Use the same version worker from unpkg CDN to avoid bundler headaches
  mod.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${mod.version}/build/pdf.worker.mjs`;
  pdfjsLib = mod;
  return mod;
}

/**
 * Import a PDF file → returns an array of image elements (one per page).
 * @param {File} file
 * @param {{ pageWidth?: number, gap?: number, startX?: number, startY?: number, onProgress?: (i, total)=>void }} opts
 */
export async function pdfToElements(file, opts = {}) {
  const pdfjs = await loadPdfJs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const total = doc.numPages;
  const targetWidth = opts.pageWidth || 700;
  const gap = opts.gap ?? 30;
  const startX = opts.startX ?? 200;
  const startY = opts.startY ?? 200;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const elements = [];
  let cursorY = startY;
  for (let n = 1; n <= total; n++) {
    const page = await doc.getPage(n);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    const dataUrl = canvas.toDataURL("image/png");
    elements.push(newImage(dataUrl, startX, cursorY, viewport.width, viewport.height, `${file.name} — page ${n}`));
    cursorY += viewport.height + gap;
    if (opts.onProgress) opts.onProgress(n, total);
  }
  return elements;
}
